// Kinio KMS 操作台的純邏輯（排序、按鈕、範圍、輪值、報到原因）；畫面與測試共用。
// 規則出處：SPEC #kinio-kms-incidents（排序、按鈕依看的人、兩條軸）、#kinio-kms-ui（家戶狀態排序、只顯示例外）、
// #kinio-kms-notify（輪值表週次輪替、空檔直接送值班主管）、#kinio-kms-onboarding（報到失敗原因與你能做什麼）。
import type { Handling, HomeState, HomeStatus, HostRow, Incident, KmgrDashboard, OncallSchedule, OncallSlot, OrgSummary, Probe, Severity, ThresholdRule, Thresholds, TimeWindow, TimeWindows } from './types';

const SEV_RANK: Record<Severity, number> = { P1: 0, P2: 1, P3: 2 };

/** 同級內：進行中未處理 → 已恢復未處理 → 進行中處理中 → 已恢復處理中 → 已結案。 */
function stageRank(i: Pick<Incident, 'now_state' | 'handling'>): number {
  if (i.handling === 'resolved') return 4;
  const handled = i.handling === 'claimed';
  const ongoing = i.now_state === 'ongoing';
  if (!handled) return ongoing ? 0 : 1;
  return ongoing ? 2 : 3;
}

/** 事故排序（清單、總覽、App 一致）：等級 → 處理階段 → 開始時間（早的在前）。 */
export function compareIncidents(a: Incident, b: Incident): number {
  return SEV_RANK[a.severity] - SEV_RANK[b.severity] || stageRank(a) - stageRank(b) || Date.parse(a.opened_at) - Date.parse(b.opened_at);
}

export function sortIncidents(list: Incident[]): Incident[] {
  return [...list].sort(compareIncidents);
}

export type IncidentFilter = 'all' | 'unclaimed' | 'mine';

/** 總覽與清單頂端的「全部 / 沒人認領 / 我處理的」。 */
export function filterIncidents(list: Incident[], f: IncidentFilter, me: string | undefined): Incident[] {
  if (f === 'unclaimed') return list.filter((i) => i.handling === 'open' || i.handling === 'acknowledged');
  if (f === 'mine') return list.filter((i) => i.handling === 'claimed' && !!me && i.claimed_by?.user_id === me);
  return list;
}

/** 未結 = 還沒結案。 */
export function isOpen(i: Pick<Incident, 'handling'>): boolean {
  return i.handling !== 'resolved';
}

export type IncidentAction = 'claim' | 'take_over' | 'acknowledge' | 'resolve';

/**
 * 按鈕依看的人而定（一顆主要鈕，SPEC #kinio-kms-incidents）：
 *   未處理 →〔我來處理〕，次要〔已看到〕〔結案…〕；已看到 →〔我來處理〕，次要〔結案…〕
 *   處理人本人 →〔標成已解決…〕（看不到〔換我處理〕）；其他人看到處理中 →〔換我處理〕
 *   已結案 → 沒有動作
 * 「已恢復，還沒人看」的主要鈕一樣是〔我來處理〕（D31）。
 */
export function incidentActions(i: Pick<Incident, 'handling' | 'claimed_by'>, me: string | undefined): { primary: IncidentAction | null; secondary: IncidentAction[] } {
  switch (i.handling) {
    case 'resolved':
      return { primary: null, secondary: [] };
    case 'claimed':
      return me && i.claimed_by?.user_id === me ? { primary: 'resolve', secondary: [] } : { primary: 'take_over', secondary: [] };
    case 'acknowledged':
      return { primary: 'claim', secondary: ['resolve'] };
    default:
      return { primary: 'claim', secondary: ['acknowledge', 'resolve'] };
  }
}

export interface StateRequest {
  handling: Handling;
  assignee?: string;
  note?: string;
  expected_version: number;
}

/** 按鈕 → POST …/state 的 body（expected_version = 畫面上的版本；衝突 409 INCIDENT_STATE_CONFLICT）。 */
export function stateRequest(action: IncidentAction, i: Pick<Incident, 'version'>, me: string, note?: string): StateRequest {
  const base = { expected_version: i.version };
  switch (action) {
    case 'claim':
    case 'take_over':
      return { ...base, handling: 'claimed', assignee: me };
    case 'acknowledge':
      return { ...base, handling: 'acknowledged' };
    case 'resolve':
      return note ? { ...base, handling: 'resolved', note } : { ...base, handling: 'resolved' };
  }
}

const HOME_RANK: Record<HomeState, number> = { bad: 0, unreadable: 1, attention: 2, unset: 3, maintenance: 4, ok: 5 };

/** 家戶狀態排序：異常 → 讀不到 → 注意 → 未設定 → 維護中 → 正常。 */
export function sortHomes(list: HomeStatus[]): HomeStatus[] {
  return [...list].sort((a, b) => HOME_RANK[a.state] - HOME_RANK[b.state] || a.home_name.localeCompare(b.home_name));
}

export const HOME_STATES: HomeState[] = ['bad', 'unreadable', 'attention', 'unset', 'maintenance', 'ok'];

/**
 * VMS 範圍 → kmgr 的 scope（all / org:<id> / site:<id> / home:<id> / view:<id>）。
 * VMS 的組織樹節點是 node:<org>:<node>；只用來源 / 分享者分的範圍（src: / ext: / sharer:）kmgr 沒有對應，送 all、畫面再用可見家戶篩。
 */
export function kmgrScopeOf(scope: string): { scope: string; clientFilter: boolean } {
  const [kind, ...rest] = scope.split(':');
  const id = rest.join(':');
  switch (kind) {
    case 'all':
      return { scope: 'all', clientFilter: false };
    case 'org':
    case 'site':
    case 'home':
      return { scope: `${kind}:${id}`, clientFilter: false };
    case 'node': {
      const node = rest.slice(1).join(':');
      return node ? { scope: 'site:' + node, clientFilter: false } : { scope: 'all', clientFilter: true };
    }
    case 'view':
      return { scope: 'view:' + id, clientFilter: false };
    default:
      return { scope: 'all', clientFilter: true };
  }
}

// ---- 輪值表（單一時段、週次輪替；M1 最小版） ----

/** 某時區此刻的日期與星期（1 = 週一 … 7 = 週日）與分鐘。 */
export function zonedParts(now: Date, tz: string): { y: number; m: number; d: number; weekday: number; minutes: number } {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(now)) p[x.type] = x.value;
  const wd = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(p.weekday) + 1;
  return { y: +p.year, m: +p.month, d: +p.day, weekday: wd, minutes: +p.hour * 60 + +p.minute };
}

const DAY_MS = 86_400_000;

/** 日期（UTC 午夜的 epoch day）。 */
function epochDay(y: number, m: number, d: number): number {
  return Math.floor(Date.UTC(y, m - 1, d) / DAY_MS);
}

/** 那天所在那週的週一（epoch day）。1970-01-01 是週四。 */
function mondayOf(day: number): number {
  const wd = ((day + 3) % 7) + 1; // 1 = 週一
  return day - (wd - 1);
}

/** 本週、下週輪到誰（rotation_start 那週 = 名單第一位；每週一 00:00（輪值表時區）換下一位）。 */
export function rotationAt(rotation: string[], rotationStart: string, tz: string, now: Date): { current: string | null; next: string | null; weekStart: Date } {
  const z = zonedParts(now, tz);
  const today = epochDay(z.y, z.m, z.d);
  const [sy, sm, sd] = rotationStart.split('-').map(Number);
  const start = sy ? mondayOf(epochDay(sy, sm, sd)) : mondayOf(today);
  const thisMon = mondayOf(today);
  const weeks = Math.floor((thisMon - start) / 7);
  const n = rotation.length;
  const at = (k: number) => (n ? rotation[(((weeks + k) % n) + n) % n] : null);
  return { current: at(0), next: at(1), weekStart: new Date(thisMon * DAY_MS) };
}

function hm(s: string): number {
  const [h, m] = s.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** 時段涵蓋整週（7 天、00:00–00:00）→ 沒有空檔。 */
export function slotCoversWeek(s: Pick<OncallSlot, 'days' | 'start' | 'end'>): boolean {
  return new Set(s.days).size === 7 && hm(s.start) === hm(s.end);
}

/** 此刻在不在這個時段裡（跨午夜的時段，凌晨那半算前一天的）。 */
export function inSlot(s: Pick<OncallSlot, 'days' | 'start' | 'end'>, tz: string, now: Date): boolean {
  const z = zonedParts(now, tz);
  const a = hm(s.start);
  const b = hm(s.end);
  if (a === b) return s.days.includes(z.weekday);
  if (a < b) return s.days.includes(z.weekday) && z.minutes >= a && z.minutes < b;
  if (z.minutes >= a) return s.days.includes(z.weekday);
  const prev = z.weekday === 1 ? 7 : z.weekday - 1;
  return z.minutes < b && s.days.includes(prev);
}

/** 此刻的值班者與值班主管；空檔 → 值班者 null（直接送值班主管）。 */
export function onDutyNow(sch: Pick<OncallSchedule, 'slots' | 'supervisor' | 'rotation_start' | 'tz'>, now: Date): { slot: OncallSlot | null; person: string | null; supervisor: string } {
  for (const s of sch.slots) {
    if (!inSlot(s, sch.tz, now)) continue;
    const r = rotationAt(s.rotation, sch.rotation_start, sch.tz, now);
    return { slot: s, person: r.current, supervisor: s.supervisor || sch.supervisor };
  }
  return { slot: null, person: null, supervisor: sch.supervisor };
}

/** 輪替起點的預設：本週一（輪值表時區）。 */
export function thisMonday(tz: string, now: Date): string {
  const z = zonedParts(now, tz);
  const mon = mondayOf(epochDay(z.y, z.m, z.d));
  return new Date(mon * DAY_MS).toISOString().slice(0, 10);
}

/** 存檔前檢查（必填與格式）；回 i18n key 清單，空 = 可以存。 */
export function validateSchedule(s: { name: string; supervisor: string; slots: OncallSlot[] }): string[] {
  const out: string[] = [];
  if (!s.name.trim()) out.push('oncall.err_name');
  if (!s.supervisor) out.push('oncall.err_supervisor');
  const slot = s.slots[0];
  if (!slot || slot.days.length === 0) out.push('oncall.err_days');
  if (slot && !/^\d{2}:\d{2}$/.test(slot.start)) out.push('oncall.err_time');
  if (slot && !/^\d{2}:\d{2}$/.test(slot.end)) out.push('oncall.err_time');
  if (slot && slot.rotation.length === 0) out.push('oncall.err_rotation');
  return [...new Set(out)];
}

// ---- 佈署權杖與報到 ----

/** 報到失敗原因（錯誤碼）→ 畫面字串 key：`enroll.reason_<x>` 與 `enroll.todo_<x>`（白話原因 + 你能做什麼）。 */
export function enrollReasonKey(code: string | undefined): string {
  switch (code) {
    case 'DEPLOY_TOKEN_EXPIRED':
      return 'expired';
    case 'DEPLOY_TOKEN_EXHAUSTED':
      return 'exhausted';
    case 'DEPLOY_TOKEN_REVOKED':
      return 'revoked';
    case 'DEPLOY_TOKEN_SOURCE_DENIED':
      return 'source';
    case 'KMGR_DPA_REQUIRED':
      return 'dpa';
    case 'ENROLL_CLOCK_SKEW':
      return 'clock';
    case 'HW_ANCHOR_MISMATCH':
    case 'AID_CLONED':
      return 'hw';
    case 'DEPLOY_TOKEN_INVALID':
      return 'invalid';
    case 'DEPLOY_TOKEN_HOST_REJECTED':
      return 'host_rejected';
    default:
      return 'other';
  }
}

/** 台數上限的預設：剩餘授權台數；剩 0（或不知道）→ 預計台數 50（SPEC #kinio-kms-onboarding「權杖建立後」）。 */
export function defaultMaxCount(seatsLeft: number | undefined): number {
  return seatsLeft && seatsLeft > 0 ? seatsLeft : 50;
}

/** 有效期限：今天起 N 天（1–30）。 */
export function expiryFrom(days: number, now: Date = new Date()): string {
  const d = Math.min(30, Math.max(1, Math.round(days)));
  return new Date(now.getTime() + d * DAY_MS).toISOString();
}

/** 來源網段欄：逗號 / 空白 / 換行分隔；回 [合法清單, 不合法的字]。 */
export function parseCidrs(text: string): [string[], string[]] {
  const ok: string[] = [];
  const bad: string[] = [];
  for (const raw of text.split(/[\s,，、]+/)) {
    const s = raw.trim();
    if (!s) continue;
    const v4 = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(\/(\d{1,2}))?$/.exec(s);
    if (v4) {
      const parts = v4.slice(1, 5).map(Number);
      const len = v4[6] === undefined ? 32 : Number(v4[6]);
      if (parts.every((p) => p <= 255) && len <= 32) {
        ok.push(s);
        continue;
      }
    } else if (/^[0-9a-f:]+(\/\d{1,3})?$/i.test(s) && s.includes(':')) {
      ok.push(s);
      continue;
    }
    bad.push(s);
  }
  return [ok, bad];
}

/** 預期清單（CSV / AD 匯出的電腦名稱）：每行第一欄、去掉標題列與重複。 */
export function parseExpectedHosts(text: string): string[] {
  const out = new Set<string>();
  for (const line of text.split(/\r?\n/)) {
    const first = line.split(/[,;\t]/)[0]?.trim().replace(/^"|"$/g, '');
    if (!first || /^(name|computer|hostname|電腦名稱|主機名稱)$/i.test(first)) continue;
    if (first.length <= 255) out.add(first);
  }
  return [...out];
}

/** 已經幾分鐘（整數，最少 0）。 */
export function minutesSince(iso: string | undefined, now: number = Date.now()): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
}

/** 預期清單的名稱跟報到的主機名稱是不是同一台（跟 kmgr 一致：不分大小寫；一邊是 FQDN、一邊是短名稱時比第一段）。 */
export function sameHost(expected: string, hostname: string): boolean {
  const e = expected.trim().toLowerCase();
  const h = hostname.trim().toLowerCase();
  if (!e || !h) return false;
  if (e === h) return true;
  const short = (x: string) => x.split('.')[0];
  return short(e) === short(h) && (!e.includes('.') || !h.includes('.'));
}

// ---- 主機（SPEC #kinio-kms-ui 導覽表「主機」欄位；#kinio-kms-alerts「預期離線不是離線」；#kinio-kms-license「未授權」） ----

/** 主機狀態標記的種類：對應 .km-st 的顏色與圖示（預期離線 ☾ 灰、更新中 ⟳ 灰、已停用 ✕ 紅）。 */
export type HostPill = 'online' | 'offline' | 'expected' | 'updating' | 'suspended' | 'revoked' | 'unknown';

/** 主機狀態：在線 ✓ / 離線 ✕ / ☾ 已關機 · 睡眠中 · 不在現場（預期離線，不開事故）/ ⟳ 更新中 / 已停用（複製）/ 已撤銷 / 不知道。 */
export function hostPill(h: Pick<HostRow, 'presence' | 'power_state' | 'state'>): { pill: HostPill; detail?: 'shutdown' | 'sleep' | 'away' } {
  if (h.state === 'suspended') return { pill: 'suspended' };
  if (h.state === 'revoked' || h.state === 'retired') return { pill: 'revoked' };
  switch (h.presence) {
    case 'online':
      return { pill: 'online' };
    case 'offline':
      return { pill: 'offline' };
    case 'shutdown':
      return { pill: 'expected', detail: 'shutdown' };
    case 'sleep':
      return { pill: 'expected', detail: 'sleep' };
    case 'expected_offline':
      return { pill: 'expected', detail: h.power_state === 'sleep' ? 'sleep' : h.power_state === 'shutdown' ? 'shutdown' : 'away' };
    case 'updating':
      return { pill: 'updating' };
    default:
      return { pill: 'unknown' };
  }
}

/** 預期離線不算離線（KMS-26：關機 / 睡眠 / 更新中 / 時段內 → 0 事故）。 */
export function isExpectedOffline(h: Pick<HostRow, 'presence' | 'state'>): boolean {
  return h.state === 'active' && (h.presence === 'shutdown' || h.presence === 'sleep' || h.presence === 'updating' || h.presence === 'expected_offline');
}

/** 主機清單的分頁：全部 / 報到中（還沒上線過的）/ 未授權。 */
export type HostsTab = 'all' | 'enrolling' | 'unlicensed';

/** 數字只在異常時著色：磁碟 > disk_pct 注意、≥ 98 異常；CPU / 記憶體 > 90 注意。 */
export function metricLevel(kind: 'cpu' | 'mem' | 'disk', pct: number | undefined, diskPct = 90): 'ok' | 'attention' | 'bad' | 'none' {
  if (pct === undefined || !Number.isFinite(pct)) return 'none';
  if (kind === 'disk') return pct >= 98 ? 'bad' : pct > diskPct ? 'attention' : 'ok';
  return pct > 90 ? 'attention' : 'ok';
}

/** 主要使用者的輸入 → PUT 的欄位：打的字等於某位組織成員的顯示名稱 → primary_user_id（kmgr 存當時的名字）；空 = 清掉；其他 = 自由文字。 */
export function primaryUserBody(text: string, members: { user_id: string; display_name?: string }[] | undefined): { primary_user: string | null; primary_user_id: string | null } {
  const name = text.trim().replace(/\s+/g, ' ');
  if (!name) return { primary_user: null, primary_user_id: null };
  const m = members?.find((x) => x.display_name && x.display_name.trim() === name);
  return { primary_user: name, primary_user_id: m ? m.user_id : null };
}

/** 主機清單搜尋：名稱或主要使用者含這段字（不分大小寫、空白分開的每段都要有）。 */
export function hostMatches(h: Pick<HostRow, 'hostname' | 'primary_user' | 'target_ref'>, q: string): boolean {
  const hay = [h.hostname, h.primary_user, h.target_ref].filter(Boolean).join(' ').toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
}

/** 主機清單篩選：類別、狀態（有異常的 = 離線 / 未授權 / 已停用 / 磁碟快滿）。 */
export function hostHasIssue(h: HostRow, diskPct = 90): boolean {
  if (h.presence === 'offline' || h.state === 'suspended' || h.license_state === 'unlicensed') return true;
  return (h.metrics?.disks ?? []).some((d) => metricLevel('disk', d.used_pct, diskPct) !== 'ok');
}

// ---- 總覽「網路與主機」（SPEC #kinio-kms-ui：每列狀態圖示 + 數字 + ›；0 的列收成「其餘 N 項正常」；下方「設定待辦」） ----

export interface NetRow {
  /** i18n key 的尾巴：net.row_<key> */
  key: 'network_down' | 'servers_offline' | 'disks_full' | 'collectors_offline' | 'devices_offline' | 'storage_faults' | 'recording_gaps' | 'ups_on_battery' | 'ups_unreadable' | 'poe_over_warn';
  n: number;
  level: 'bad' | 'attention' | 'unk';
  to: string;
}

/**
 * 網路與主機的列。資料來源：探針失敗（WAN / 交換器 / AP 沒有回應）、伺服器離線（主機數）、收集器離線、Kinio 設備離線 / 儲存異常 / 錄影缺口（HEALTH-01 一套定義）。
 * 網路區讀不到（KMS-25）時探針那一列不算（是失聯前的舊資料），由呼叫端畫 ⊘。磁碟快滿 = kmgr disk_full 告警還開著的主機（0.22.0 起）；TLS 到期總覽還沒有數字，不列。
 */
export function networkRows(d: KmgrDashboard): { rows: NetRow[]; okRows: number } {
  const all: NetRow[] = [];
  const net = d.network;
  const netReadable = net && net.source_state === 'ok';
  if (net && net.source_state !== 'unset') all.push({ key: 'network_down', n: netReadable ? net.probes_failing : 0, level: 'bad', to: '/kmgr/network' });
  all.push({ key: 'servers_offline', n: d.hosts.offline, level: 'bad', to: '/kmgr/hosts?state=offline' });
  if (d.hosts.disk_full !== undefined) all.push({ key: 'disks_full', n: d.hosts.disk_full, level: 'attention', to: '/kmgr/hosts?issue=1' });
  if (net && net.source_state !== 'unset') all.push({ key: 'collectors_offline', n: net.collectors_offline.length, level: 'attention', to: '/kmgr/network?tab=collectors' });
  if (d.kinio && d.kinio.source_state !== 'unavailable') {
    all.push({ key: 'devices_offline', n: d.kinio.offline_devices, level: 'bad', to: '/devices' });
    all.push({ key: 'storage_faults', n: d.kinio.storage_faults, level: 'bad', to: '/devices' });
    all.push({ key: 'recording_gaps', n: d.kinio.recording_gaps, level: 'attention', to: '/playback' });
  }
  // 電源（能源 NRG-K5）：UPS 用電池中、讀不到（獨立一列，不算進「都用市電」）、PoE 超過注意門檻；0 時收進「其餘正常」
  if (d.power && d.power.ups_total > 0) {
    all.push({ key: 'ups_on_battery', n: d.power.ups_on_battery, level: 'bad', to: '/kmgr/network?tab=ups' });
    all.push({ key: 'ups_unreadable', n: d.power.ups_unreadable, level: 'unk', to: '/kmgr/network?tab=ups' });
  }
  if (d.power?.poe_over_warn) all.push({ key: 'poe_over_warn', n: d.power.poe_over_warn, level: 'attention', to: '/kmgr/incidents' });
  const rows = all.filter((r) => r.n > 0);
  return { rows, okRows: all.length - rows.length - (net && net.source_state === 'unreadable' ? 1 : 0) };
}

/** 設定待辦（不是故障、不算進家戶狀態）：未授權的電腦；金鑰未受硬體保護、本機已暫停 kmgr 總覽還沒有數字。 */
export function setupTodo(d: KmgrDashboard): { key: 'unlicensed' | 'ups_no_downstream'; n: number; to: string }[] {
  const out: { key: 'unlicensed' | 'ups_no_downstream'; n: number; to: string }[] = [];
  if (d.hosts.unlicensed > 0) out.push({ key: 'unlicensed', n: d.hosts.unlicensed, to: '/kmgr/hosts?tab=unlicensed' });
  // 還沒登記下游的 UPS（NRG-K3：停電時下游會各自開事故）
  if (d.power?.ups_no_downstream) out.push({ key: 'ups_no_downstream', n: d.power.ups_no_downstream, to: '/kmgr/network?tab=ups' });
  return out;
}

// ---- 探針 / 網路設備（M2-04） ----

/** 網路設備 = ping / snmp 探針；應用探針 = http / tcp / file。 */
export function isNetworkDevice(p: Pick<Probe, 'kind'>): boolean {
  return p.kind === 'ping' || p.kind === 'snmp';
}

export type ProbeState = 'ok' | 'bad' | 'unassigned' | 'noresult' | 'unreadable';

/** 探針的狀態：讀不到（收集器失聯，結果是舊的）優先；沒有收集器負責 → 未設定；還沒有結果 → 更新中；失敗 → 異常。 */
export function probeState(p: Pick<Probe, 'collector' | 'last_at' | 'last_up' | 'last_error'>, networkUnreadable: boolean): ProbeState {
  if (!p.collector) return 'unassigned';
  if (networkUnreadable) return 'unreadable';
  if (!p.last_at) return 'noresult';
  return p.last_up && !p.last_error ? 'ok' : 'bad';
}

/** 新增 / 修改探針的檢查；回 i18n key 清單（probe.err_*）。 */
export function validateProbe(p: { kind: string; name?: string; target?: string; port?: number; http?: { path?: string }; file?: { path?: string } }): string[] {
  const out: string[] = [];
  if (p.kind !== 'file' && !p.target?.trim()) out.push('probe.err_target');
  if (p.kind === 'tcp' && (!p.port || p.port < 1 || p.port > 65535)) out.push('probe.err_port');
  if (p.kind === 'http' && !p.http?.path?.startsWith('/')) out.push('probe.err_path');
  if (p.kind === 'file' && !p.file?.path?.trim()) out.push('probe.err_file');
  return out;
}

/** 收集器網段欄：逗號 / 空白分隔，每筆要是 CIDR（a.b.c.d/n）；回 [合法, 不合法]。 */
export function parseCidrList(text: string): [string[], string[]] {
  const [ok, bad] = parseCidrs(text);
  const strict = ok.filter((c) => c.includes('/'));
  return [strict, [...bad, ...ok.filter((c) => !c.includes('/'))]];
}

// ---- 時段（維護 / 營業 / 主機預期離線；kmgr TimeWindows） ----

/** 時段檢查：至少一天、時間格式 HH:MM；回 i18n key（window.err_*）。 */
export function validateWindows(ws: TimeWindow[]): string[] {
  const out: string[] = [];
  for (const w of ws) {
    if (!w.days.length) out.push('window.err_days');
    if (!/^\d{2}:\d{2}$/.test(w.start) || !/^\d{2}:\d{2}$/.test(w.end)) out.push('window.err_time');
  }
  return [...new Set(out)];
}

/** 「週一–週五 09:00–18:00」的星期部分：連續的寫成區間，其他用「、」。dayName(1..7)。 */
export function daysText(days: number[], dayName: (d: number) => string): string {
  const ds = [...new Set(days)].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b);
  if (ds.length === 7) return '';
  const parts: string[] = [];
  let i = 0;
  while (i < ds.length) {
    let j = i;
    while (j + 1 < ds.length && ds[j + 1] === ds[j] + 1) j++;
    parts.push(j - i >= 2 ? `${dayName(ds[i])}–${dayName(ds[j])}` : ds.slice(i, j + 1).map(dayName).join('、'));
    i = j + 1;
  }
  return parts.join('、');
}

/** 一個時段的一句：「週一–週五 09:00–18:00」「每天 整天」（start = end）。 */
export function windowText(w: TimeWindow, dayName: (d: number) => string, every: string, allDay: string): string {
  const d = daysText(w.days, dayName) || every;
  return `${d} ${w.start === w.end ? allDay : `${w.start}–${w.end}`}`;
}

export function windowsText(tw: TimeWindows | undefined, dayName: (d: number) => string, every: string, allDay: string): string {
  return (tw?.windows ?? []).map((w) => windowText(w, dayName, every, allDay)).join('；');
}

/** 一鍵維護的時數選項（小時；kmgr 1–72）。 */
export const MAINTENANCE_HOURS = [1, 2, 4, 8, 24, 72];

/** 預設的營業時段（週一–週五 09:00–18:00）、維護排程（週日 02:00–04:00）給表單新增一列用。 */
export const DEFAULT_BUSINESS_WINDOW: TimeWindow = { days: [1, 2, 3, 4, 5], start: '09:00', end: '18:00' };
export const DEFAULT_MAINTENANCE_WINDOW: TimeWindow = { days: [7], start: '02:00', end: '04:00' };

// ---- 門檻與等級（SPEC #kinio-kms-alerts「預設等級與防抖」；預設表同 kmgr internal/thresholds，kmgr 的 GET 回有效值） ----

export const THRESHOLD_KEYS = ['home_unreachable', 'camera_not_recording', 'recorder_storage', 'critical_host', 'network_device', 'server_offline', 'disk_tls_probe', 'unreadable', 'enroll_failed', 'workstation_offline'] as const;
export type ThresholdKey = (typeof THRESHOLD_KEYS)[number];

export function defaultThresholdRules(): ThresholdRule[] {
  return [
    { key: 'home_unreachable', severity: 'P1', debounce_min: 5 },
    { key: 'camera_not_recording', severity: 'P1', debounce_min: 5 },
    { key: 'recorder_storage', severity: 'P1', debounce_min: 0 },
    { key: 'critical_host', severity: 'P1', debounce_min: 3 },
    { key: 'network_device', severity: 'P1', debounce_min: 3 },
    { key: 'server_offline', severity: 'P2', debounce_min: 5 },
    { key: 'disk_tls_probe', severity: 'P2', debounce_min: 15 },
    { key: 'unreadable', severity: 'P2', debounce_min: 30 },
    { key: 'enroll_failed', severity: 'P2', debounce_min: 0 },
    { key: 'workstation_offline', severity: 'P3', debounce_min: null },
  ];
}

export function defaultThresholds(homeId: string): Thresholds {
  return { home_id: homeId, rules: defaultThresholdRules(), disk_pct: 90, tls_days: 14, workstation_offline_days: 7 };
}

/** 哪些列跟預設不一樣（畫面標「已改」）。 */
export function overriddenRules(rules: ThresholdRule[]): Set<string> {
  const def = new Map(defaultThresholdRules().map((r) => [r.key, r]));
  return new Set(rules.filter((r) => def.get(r.key) && (def.get(r.key)!.severity !== r.severity || def.get(r.key)!.debounce_min !== r.debounce_min)).map((r) => r.key));
}

/** 門檻數字檢查（同 kmgr）：磁碟 50–99、TLS 1–90 天、工作站離線 7–90 天（只能比預設長）。 */
export function validateThresholds(t: Pick<Thresholds, 'disk_pct' | 'tls_days' | 'workstation_offline_days'>): string[] {
  const out: string[] = [];
  if (!(t.disk_pct >= 50 && t.disk_pct <= 99)) out.push('threshold.err_disk');
  if (!(t.tls_days >= 1 && t.tls_days <= 90)) out.push('threshold.err_tls');
  if (!(t.workstation_offline_days >= 7 && t.workstation_offline_days <= 90)) out.push('threshold.err_ws');
  return out;
}

/** 防抖可選的值（null = 不開事故）。 */
export const DEBOUNCE_OPTIONS: (number | null)[] = [0, 3, 5, 15, 30, 60, null];

/** 影響錄影、不能設成不開事故的列（同 kmgr；SPEC #kinio-kms-notify「防止靜默」）。 */
const RECORDING_KEYS = new Set<string>(['home_unreachable', 'camera_not_recording', 'recorder_storage']);

/**
 * 一列可選的防抖（同 kmgr 的 PUT 檢查）：只能比預設長（評估器以內建防抖為下限）、影響錄影的不能關、
 * 報到失敗只有立即 / 不開事故、工作站離線用天數（只有 null）。目前的值不在選項裡時也列進去（不讓畫面把它吃掉）。
 */
export function debounceOptions(key: string, current: number | null): (number | null)[] {
  const def = defaultThresholdRules().find((r) => r.key === key);
  let out: (number | null)[];
  if (key === 'workstation_offline') out = [null];
  else if (key === 'enroll_failed') out = [0, null];
  else out = DEBOUNCE_OPTIONS.filter((d) => (d === null ? !RECORDING_KEYS.has(key) : def?.debounce_min == null || d >= def.debounce_min));
  if (!out.includes(current)) out = [...out, current].sort((a, b) => (a === null ? 1 : b === null ? -1 : a - b));
  return out;
}

// ---- 遙測白名單（SPEC #kinio-kms-telemetry「指標白名單」；主機頁「回報了什麼」） ----

/** 管理程式只收這些（i18n key telemetry.item_<key>）；清單是規格的白名單，不是這台實際送了什麼。 */
export const TELEMETRY_WHITELIST = ['cpu_mem_disk', 'net_bytes', 'uptime_power', 'services', 'os_update', 'event_log'] as const;
/** 一律不收（沒有開關）。 */
export const TELEMETRY_NEVER = ['cmdline', 'window_title', 'files', 'connections', 'images'] as const;

// ---- 現場資訊 ----

/** 電話欄：只留數字、+、-、空白、括號；回 [合法, 顯示用] */
export function normalizePhone(s: string): string | null {
  const v = s.trim();
  if (!v) return '';
  return /^[+\d][\d\s\-()]{5,}$/.test(v) ? v : null;
}

// ---- 跨組織範圍（M5-04；SPEC #kinio-kms-ui「跨組織範圍」） ----

/** 組織篩選最多幾個（= kmgr 一次最多 20 個 scope）。 */
export const MAX_ORG_FILTER = 20;

/** 範圍 + 組織篩選 → API 的 scope（範圍是「全部」而且有選組織 → 多個 org:<id>；其他照範圍）。 */
export function kmgrScopes(scope: string, orgFilter: string[]): string | string[] {
  if (scope !== 'all' || orgFilter.length === 0) return scope;
  return orgFilter.slice(0, MAX_ORG_FILTER).map((o) => 'org:' + o);
}

/** URL 的 org 參數（逗號分隔）→ 組織 id 清單（去重、去空）。 */
export function parseOrgFilter(v: string | null): string[] {
  return [...new Set((v ?? '').split(',').map((x) => x.trim()).filter(Boolean))].slice(0, MAX_ORG_FILTER);
}

/** 這個組織有沒有要看的事（沒人認領的 P1、異常 / 讀不到的家戶、未結事故、離線主機）。 */
export function orgNeedsAttention(o: OrgSummary): boolean {
  return o.p1_unclaimed > 0 || (o.homes_by_state.bad ?? 0) > 0 || (o.homes_by_state.unreadable ?? 0) > 0 || o.incidents_open > 0 || o.hosts_offline > 0;
}

/** 同 kmgr 的排序（多個 plane 合併後再排一次）：沒人認領的 P1 → 異常家戶 → 讀不到 → 未結事故 → 離線主機，再依名稱。 */
export function sortOrgSummaries(list: OrgSummary[]): OrgSummary[] {
  const key = (o: OrgSummary) => [o.p1_unclaimed, o.homes_by_state.bad ?? 0, o.homes_by_state.unreadable ?? 0, o.incidents_open, o.hosts_offline];
  return [...list].sort((a, b) => {
    const ka = key(a);
    const kb = key(b);
    for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return kb[i] - ka[i];
    return (a.org_name ?? a.org_id).localeCompare(b.org_name ?? b.org_id);
  });
}

/** 「網路與主機」依組織的一列要寫的事（SPEC #kinio-kms-ui「網路與主機」範圍跨組織時依組織分組；kmgr by_org[].network / hosts_disk_full）。 */
export interface OrgNetIssue {
  key: 'unreadable' | 'net_down' | 'servers_offline' | 'disks_full' | 'collectors_offline' | 'ups_on_battery' | 'poe_over_warn';
  n: number;
}

export function orgNetIssues(o: OrgSummary): OrgNetIssue[] {
  const out: OrgNetIssue[] = [];
  const net = o.network;
  if (net?.source_state === 'unreadable') out.push({ key: 'unreadable', n: net.unreadable_homes });
  else if (net?.probes_failing) out.push({ key: 'net_down', n: net.probes_failing });
  if (o.hosts_offline) out.push({ key: 'servers_offline', n: o.hosts_offline });
  if (o.hosts_disk_full) out.push({ key: 'disks_full', n: o.hosts_disk_full });
  if (net?.collectors_offline) out.push({ key: 'collectors_offline', n: net.collectors_offline });
  if (o.ups_on_battery) out.push({ key: 'ups_on_battery', n: o.ups_on_battery });
  if (o.poe_over_warn) out.push({ key: 'poe_over_warn', n: o.poe_over_warn });
  return out;
}

/** 依組織的網路與主機：有事的組織（照 kmgr 的組織排序）前 10 個，其餘正常 / 還有幾個有事。 */
export function byOrgNetRows(list: OrgSummary[]): { shown: { org: OrgSummary; issues: OrgNetIssue[] }[]; othersOk: number; othersMore: number } {
  const withIssues = sortOrgSummaries(list)
    .map((org) => ({ org, issues: orgNetIssues(org) }))
    .filter((x) => x.issues.length > 0);
  const shown = withIssues.slice(0, BY_ORG_TOP);
  return { shown, othersOk: list.length - withIssues.length, othersMore: withIssues.length - shown.length };
}

/** 總覽「依組織」最多列幾個有事的組織（SPEC：前 10 個 +「其餘 N 個組織正常」）。 */
export const BY_ORG_TOP = 10;

/** 要列的組織與剩下的：先列有事的（最多 10 個），剩下的若都正常 → othersOk，否則 othersMore。 */
export function byOrgRows(list: OrgSummary[]): { shown: OrgSummary[]; othersOk: number; othersMore: number } {
  const sorted = sortOrgSummaries(list);
  const shown = sorted.filter(orgNeedsAttention).slice(0, BY_ORG_TOP);
  const rest = sorted.filter((o) => !shown.includes(o));
  return rest.some(orgNeedsAttention) ? { shown, othersOk: 0, othersMore: rest.length } : { shown, othersOk: rest.length, othersMore: 0 };
}
